import { useCallback, useState, useMemo } from 'react';
import clsx from 'clsx';
import { AmountUtils, CurrencyUtils, PaletteUtils } from '@shared/utils';
import { type AccountResponse, AccountType } from '@shared/api/rest/accounts';
import { Modal } from '../modal';
import { ColorsEnum, Text } from '../text';
import { Icon } from '../icon';
import DollarIcon from '@assets/icons/dollar.svg';
import SwapIcon from '@assets/icons/swap.svg';
import { NoData } from '../noData';

interface SelectAccountProps<CanCreate> {
  label?: React.ReactNode;
  loading?: boolean;
  canCreate?: CanCreate;
  type?: AccountType;
  currentAccount?: AccountResponse | null;
  accounts: AccountResponse[];
  onChange?: (value: AccountResponse) => void;
  balanceFieldName?: string;
}

const defaultTitle = 'Select account';

export const SelectAccount = <CanCreate extends boolean | undefined>(props: SelectAccountProps<CanCreate>) => {
  const [open, setOpen] = useState<boolean>(false);

  const onSelect = useCallback(
    (value: AccountResponse) => {
      props.onChange && props.onChange(value);
      setOpen(false);
    },
    [props.onChange],
  );

  const title = useMemo(() => props.label ?? defaultTitle, [props.label]);
  const accountsPaymentList = props.type ? props.accounts.filter((account) => account.type === props.type) : props.accounts;

  return (
    <>
      <Text className='mb-3'>{title}</Text>
      <AccountListItem
        className='bg-ice dark:bg-eerie-black'
        account={props.currentAccount}
        isShowEndIcon
        onClick={() => setOpen(true)}
        balanceFieldName={props.balanceFieldName}
      />
      <Modal isOpen={open} title={title as string} onClose={() => setOpen(false)}>
        {/* <input
            className="border-b border-light outline-none w-full h-10"
            type="text"
            placeholder="Search"
          /> */}
        {accountsPaymentList.length < 1 ? (
          <NoData />
        ) : (
          accountsPaymentList.map((x, key) => (
            <AccountListItem
              className="dark:bg-slate bg-ice font-['Prata'] w-[456px]"
              key={key}
              account={x}
              isShowEndIcon={false}
              onClick={() => onSelect(x)}
              balanceFieldName={props.balanceFieldName}
              titleClassName='text-xl'
            />
          ))
        )}
      </Modal>
    </>
  );
};

interface IAccountListItemProps {
  className?: string;
  account?: AccountResponse | null;
  isShowEndIcon?: boolean;
  onClick: () => void;
  balanceFieldName?: string;
  titleClassName?: string;
}

const AccountListItem = (props: IAccountListItemProps) => {
  const currencySymbol = useMemo(() => {
    if (!props.account) return '';
    return CurrencyUtils.getSymbolByCode(props.account.currency);
  }, [props.account]);

  return (
    <div className={clsx('flex flex-row my-1 p-3 cursor-pointer', props.className)} onClick={props.onClick}>
      <div
        className={clsx(
          'w-[68px] h-[42px] bg-soft-black flex items-center justify-center rounded-[5px] text-2xl',
          PaletteUtils.isInsigniaPalette && 'text-primary',
          PaletteUtils.isVcfcPalette && 'text-white',
        )}
      >
        {currencySymbol ? currencySymbol : <Icon icon={<DollarIcon />} color='inherit' />}
      </div>
      <div className='flex flex-col justify-between ml-3 my-auto'>
        {props.account ? (
          <>
            <Text className={props.titleClassName}>
              {props.balanceFieldName
                ? AmountUtils.transformToAmountWithCurrency(props?.account?.[props.balanceFieldName as keyof AccountResponse] as number)
                : AmountUtils.transformToAmountWithCurrency(props.account.balance)}
              {currencySymbol}
            </Text>
            <Text weight='semibold' size='xs' color={ColorsEnum.Secondary}>
              {props.account.productName} {props.account.accountNumber}
            </Text>
          </>
        ) : (
          <Text>Select account...</Text>
        )}
      </div>
      {props.isShowEndIcon && (
        <div className='ml-auto my-auto flex gap-3 items-center'>
          {props.account ? (
            <div className='min-w-16 w-16 max-w-16 h-7 p-2 rounded border border-primary flex items-center justify-center'>
              <Text className='mr-2' size='xs' weight='semibold' color={ColorsEnum.Primary}>
                {CurrencyUtils.getSymbolByCode(props.account?.currency)}
              </Text>
              <Text size='xs' weight='semibold' isUppercase color={ColorsEnum.Primary}>
                {props.account.mainAccId === null ? 'Main' : 'Sub'}
              </Text>
            </div>
          ) : (
            ''
          )}
          <Icon icon={<SwapIcon width='24' height='24' />} className='rotate-90' />
        </div>
      )}
    </div>
  );
};
